/**
 * Reusable message content for use inside ReusableDialog (or any container).
 * Renders message text, optional Details toggle, and OK button. No dialog shell.
 */
import React, { useState } from "react";
import { DefaultButton, SpinnerSize } from "@fluentui/react";
import { useTheme } from "../../theme/OAS365Theme";
import CustomSpinner from "../CustomSpinner";

/**
 * @param {string} [message] - Main message text
 * @param {string} [errorMessage] - Optional details (shown with "Details" toggle)
 * @param {boolean} [showDetailsButton=true] - Show "Details" when errorMessage is present
 * @param {boolean} [showOkButton=true] - Show/hide OK button
 * @param {() => void} onClose - Called when OK is clicked
 * @param {boolean} [isLoading=false] - Show spinner instead of message
 * @param {string} [loadingText] - Text next to spinner when isLoading
 */
export function MessageBoxContent({
  message = "",
  errorMessage = "",
  showDetailsButton = true,
  showOkButton = true,
  onClose,
  isLoading = false,
  loadingText = "",
}) {
  const themeConfig = useTheme();
  const [isMoreDetails, setIsMoreDetails] = useState(false);
  const [moreDetailsLabel, setMoreDetailsLabel] = useState("Details");

  const handleClose = () => {
    setIsMoreDetails(false);
    setMoreDetailsLabel("Details");
    onClose?.();
  };

  const toggleDetails = () => {
    const next = !isMoreDetails;
    setIsMoreDetails(next);
    setMoreDetailsLabel(next ? "Hide Details" : "Details");
  };

  const errorLines =
    typeof errorMessage === "string" && errorMessage
      ? errorMessage.split(/\r\n|\n|\r/gm)
      : [];
  const hasDetails = showDetailsButton && errorLines.length > 0;

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          padding: "6px 0",
        }}
      >
        {loadingText && (
          <span
            style={{
              color: themeConfig.colors.fontColor,
              fontWeight: themeConfig.fontWeight.titleWeight,
            }}
          >
            {loadingText}
          </span>
        )}
        <CustomSpinner size={SpinnerSize.large} />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <p
        style={{
          maxWidth: 435,
          width: "100%",
          margin: 0,
          fontWeight: themeConfig.fontWeight.titleWeight,
          fontSize: `${themeConfig.contextMenu?.fontSize ?? themeConfig.fontSizes?.normal ?? 12}px`,
          fontFamily:
            themeConfig.contextMenu?.fontFamily ??
            themeConfig.fonts?.regular ??
            "Poppins, sans-serif",
          lineHeight: 1.4,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        {message}
        {hasDetails && (
          <>
            <br />
            {isMoreDetails && (
              <span
                style={{
                  fontSize: "small",
                  color: "red",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {errorLines.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </span>
            )}
          </>
        )}
      </p>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
        {hasDetails && (
          <DefaultButton
            style={{
              marginTop: 0,
              borderRadius: 25,
              fontFamily: themeConfig.fonts.regular,
              fontWeight: themeConfig.fontWeight.menuWeight,
              border: "1px solid #4b8d6b",
              backgroundColor: "#FFFFFF",
              color: "#4b8d6b",
            }}
            text={moreDetailsLabel}
            onClick={toggleDetails}
          />
        )}

        {showOkButton && (
          <DefaultButton
            style={{
              marginTop: 0,
              color: themeConfig.colors.buttonTextColor,
              backgroundColor: themeConfig.colors.themeDarkColor,
              borderRadius: 25,
              fontSize: `${themeConfig.contextMenu?.fontSize ?? themeConfig.fontSizes?.normal ?? 12}px`,
              fontFamily:
                themeConfig.contextMenu?.fontFamily ??
                themeConfig.fonts?.regular ??
                "Poppins, sans-serif",
              fontWeight: themeConfig.fontWeight.menuWeight,
            }}
            text="OK"
            onClick={handleClose}
          />
        )}
      </div>
    </div>
  );
}

export default MessageBoxContent;
